// ============================================================================
// 변형 가능한 메시 — 카드(앞/뒤/옆면, 휘어짐) 와 팩 조각(지글거리는 절단선, 틈, 융기)
// 모든 변형은 CPU 에서 매 프레임 rest 좌표 → 현재 좌표로 계산한다. UV 는 rest 기준 고정.
// ============================================================================
import * as THREE from 'three';

// ------------------------------------------------------------------ 셰이더
// 텍스처는 평평(unlit). 광택/음영은 코드가 법선으로 얹는다.
const VERT = /* glsl */ `
varying vec2 vUv;
varying vec3 vN;
varying vec3 vP;
void main() {
  vUv = uv;
  vN = normalize(normalMatrix * normal);
  vec4 mv = modelViewMatrix * vec4(position, 1.0);
  vP = mv.xyz;
  gl_Position = projectionMatrix * mv;
}`;

const FRAG = /* glsl */ `
uniform sampler2D map;
uniform float gloss;
uniform vec3 lightDir;
uniform float bandPos;
uniform float bandWidth;
uniform float bandStrength;
uniform vec3 bandTint;
uniform float flash;
uniform float shade;
uniform float alphaCut;
varying vec2 vUv;
varying vec3 vN;
varying vec3 vP;
// 평평하게 카메라를 향한 면은 텍스처 원본 그대로. 기울어진 부분만 밝아지거나 어두워진다.
// (구워진 조명이 없는 텍스처 위에 코드가 광택을 얹는다 — §12.7)
//
// bandTint / flash 는 AR·UR 등장 연출이 쓴다 (params.ts P.reveal). 기본값은 흰색·0 이라
// 바인더 시트 광택(setBand)과 예전 동작이 **한 픽셀도 안 바뀐다**: vec3(1)*band == vec3(band).
void main() {
  vec4 c = texture2D(map, vUv);
  if (c.a < alphaCut) discard;
  vec3 n = normalize(vN);
  if (!gl_FrontFacing) n = -n;
  vec3 v = normalize(-vP);
  vec3 l = normalize(lightDir);
  vec3 h = normalize(l + v);
  vec3 nf = vec3(0.0, 0.0, 1.0);
  float spec = pow(max(dot(n, h), 0.0), 20.0) - pow(max(dot(nf, h), 0.0), 20.0);
  float diff = shade * (dot(n, l) - l.z);
  float band = 0.0;
  if (bandStrength > 0.0) {
    float d = (vUv.y + vUv.x * 0.35 - bandPos) / bandWidth;
    band = exp(-d * d) * bandStrength;
  }
  c.rgb = c.rgb * (1.0 + diff) + vec3(spec * gloss) + bandTint * (band + flash);
  gl_FragColor = c;
  #include <colorspace_fragment>
}`;

export interface SurfaceUniforms {
  map: { value: THREE.Texture };
  gloss: { value: number };
  lightDir: { value: THREE.Vector3 };
  bandPos: { value: number };
  bandWidth: { value: number };
  bandStrength: { value: number };
  bandTint: { value: THREE.Color };
  flash: { value: number };
  shade: { value: number };
  alphaCut: { value: number };
}

export function makeSurfaceMaterial(map: THREE.Texture, gloss = 0.6, shade = 0.3): THREE.ShaderMaterial {
  const uniforms: SurfaceUniforms = {
    map: { value: map },
    gloss: { value: gloss },
    lightDir: { value: new THREE.Vector3(0.3, 0.7, 0.9).normalize() },
    bandPos: { value: -10 },
    bandWidth: { value: 0.08 },
    bandStrength: { value: 0 },
    bandTint: { value: new THREE.Color(1, 1, 1) },
    flash: { value: 0 },
    shade: { value: shade },
    alphaCut: { value: 0.4 },
  };
  return new THREE.ShaderMaterial({
    uniforms: uniforms as unknown as { [k: string]: THREE.IUniform },
    vertexShader: VERT,
    fragmentShader: FRAG,
    transparent: true,
    depthWrite: true,
    side: THREE.FrontSide,
  });
}

// ------------------------------------------------------------------ 카드
export type Deform = (x: number, y: number, out: Float64Array) => void; // out: [x, y, z]

export class CardMesh {
  geo: THREE.BufferGeometry;
  mesh: THREE.Mesh;
  rest: Float32Array; // rest x,y per vertex (2 per vert)
  zSign: Float32Array; // +1 front, -1 back, 0 rim uses rimZ
  rimZ: Float32Array;
  pos: THREE.BufferAttribute;
  nVerts: number;
  matFront: THREE.ShaderMaterial;
  matBack: THREE.ShaderMaterial;
  matRim: THREE.MeshBasicMaterial;

  constructor(
    public w: number,
    public h: number,
    public t: number,
    frontTex: THREE.Texture,
    backTex: THREE.Texture,
    nx = 2,
    ny = 40,
    cornerRFrac = 0.035, // 카드 폭 대비 모서리 반경. cardgen 의 border-radius:3.5% 와 같은 값이다
    //   (절대값으로 두면 카드 크기를 바꿀 때 같이 안 커진다)
    rimN = 72,
    // 카드 텍스처는 이제 **여백 없이 꽉 찬** 1024×1432 다 (cardgen 규격. 둥근 모서리만 투명하다).
    // 메시 종횡비도 같은 1.3984 라(params 의 BASE.cardH) 그림과 메시가 1:1 로 맞는다 — UV 를
    // 잘라낼 이유가 없어졌다. 옛 애셋(1024×1536, 좌우 20px / 상하 24px 투명 여백)일 때는
    // 메시가 여백까지 덮어 그 띠에서 카드가 뚫려 뒤 카드가 비쳤고, 그래서 안쪽으로 잘랐었다.
    uvInsetU = 0,
    uvInsetV = 0,
  ) {
    const positions: number[] = [];
    const uvs: number[] = [];
    const rest: number[] = [];
    const zSign: number[] = [];
    const rimZ: number[] = [];
    const index: number[] = [];

    // 앞면(+z, 뒤집기 전 카메라를 향하는 면 = 카드 뒷면 텍스처) 과 뒷면(-z, 카드 앞면 텍스처)
    const grid = (sign: number) => {
      const base = positions.length / 3;
      for (let j = 0; j <= ny; j++) {
        for (let i = 0; i <= nx; i++) {
          const x = -w / 2 + (w * i) / nx;
          const y = -h / 2 + (h * j) / ny;
          positions.push(x, y, (sign * t) / 2);
          const u = uvInsetU + (1 - 2 * uvInsetU) * (i / nx);
          const v = uvInsetV + (1 - 2 * uvInsetV) * (j / ny);
          // -z 면은 X축 π 회전 후 똑바로 보이도록 v 를 뒤집는다 (u 는 그대로)
          uvs.push(u, sign > 0 ? v : 1 - v);
          rest.push(x, y);
          zSign.push(sign);
          rimZ.push(0);
        }
      }
      for (let j = 0; j < ny; j++) {
        for (let i = 0; i < nx; i++) {
          const a = base + i + j * (nx + 1);
          const b = a + 1;
          const c = a + (nx + 1);
          const d = c + 1;
          if (sign > 0) index.push(a, b, d, a, d, c);
          else index.push(a, d, b, a, c, d);
        }
      }
      return positions.length / 3 - base;
    };
    grid(+1);
    const backStart = positions.length / 3;
    grid(-1);
    const backCount = positions.length / 3 - backStart;
    void backCount;

    // 옆면: 라운드 사각 윤곽을 따라 앞/뒤 링
    const rimStart = positions.length / 3;
    const path: [number, number][] = [];
    const r = Math.min(cornerRFrac * w, w / 2, h / 2);
    const perCorner = Math.max(3, Math.floor(rimN / 4));
    const corners: [number, number, number][] = [
      [w / 2 - r, -h / 2 + r, -Math.PI / 2],
      [w / 2 - r, h / 2 - r, 0],
      [-w / 2 + r, h / 2 - r, Math.PI / 2],
      [-w / 2 + r, -h / 2 + r, Math.PI],
    ];
    for (const [cx, cy, a0] of corners) {
      for (let k = 0; k <= perCorner; k++) {
        const a = a0 + (Math.PI / 2) * (k / perCorner);
        path.push([cx + r * Math.cos(a), cy + r * Math.sin(a)]);
      }
    }
    const M = path.length;
    for (const [x, y] of path) {
      positions.push(x, y, t / 2);
      uvs.push(0.5, 0.5);
      rest.push(x, y);
      zSign.push(0);
      rimZ.push(t / 2);
    }
    for (const [x, y] of path) {
      positions.push(x, y, -t / 2);
      uvs.push(0.5, 0.5);
      rest.push(x, y);
      zSign.push(0);
      rimZ.push(-t / 2);
    }
    for (let k = 0; k < M; k++) {
      const k1 = (k + 1) % M;
      const f0 = rimStart + k, f1 = rimStart + k1;
      const b0 = rimStart + M + k, b1 = rimStart + M + k1;
      index.push(f0, b0, b1, f0, b1, f1);
    }
    const rimCount = index.length; // placeholder; groups set below

    const geo = new THREE.BufferGeometry();
    this.pos = new THREE.BufferAttribute(new Float32Array(positions), 3);
    this.pos.setUsage(THREE.DynamicDrawUsage);
    geo.setAttribute('position', this.pos);
    geo.setAttribute('uv', new THREE.BufferAttribute(new Float32Array(uvs), 2));
    geo.setIndex(index);
    const frontTris = nx * ny * 6;
    geo.addGroup(0, frontTris, 0);
    geo.addGroup(frontTris, frontTris, 1);
    geo.addGroup(frontTris * 2, rimCount - frontTris * 2, 2);
    geo.computeVertexNormals();
    this.geo = geo;
    this.rest = new Float32Array(rest);
    this.zSign = new Float32Array(zSign);
    this.rimZ = new Float32Array(rimZ);
    this.nVerts = positions.length / 3;

    this.matFront = makeSurfaceMaterial(backTex, 0.6, 0.3); // +z 면 = 카드 뒷면
    this.matBack = makeSurfaceMaterial(frontTex, 0.6, 0.3); // -z 면 = 카드 앞면
    this.matRim = new THREE.MeshBasicMaterial({ color: 0xe9e2cf, side: THREE.DoubleSide });
    this.mesh = new THREE.Mesh(geo, [this.matFront, this.matBack, this.matRim]);
    this.mesh.rotation.order = 'ZYX';
    this.mesh.frustumCulled = false;
  }

  setFrontTexture(tex: THREE.Texture): void {
    (this.matBack.uniforms as unknown as SurfaceUniforms).map.value = tex;
  }
  setBackTexture(tex: THREE.Texture): void {
    (this.matFront.uniforms as unknown as SurfaceUniforms).map.value = tex;
  }

  /**
   * AR·UR 등장 연출 — **앞면(-z, 카드 그림 쪽) 셰이더에만** 빛을 얹는다. 뒷면은 안 건드린다.
   * 지오메트리도 위치도 안 바뀌므로 이 카드를 잡는 드래그가 연출 때문에 끊기지 않는다.
   * 값은 전부 sim 이 계산해 RS 로 넘긴 것이다 (render 는 여기에 꽂기만 한다).
   */
  setFrontFx(bandPos: number, bandWidth: number, bandStrength: number, flash: number, tint: readonly [number, number, number]): void {
    const u = this.matBack.uniforms as unknown as SurfaceUniforms;
    u.bandPos.value = bandPos;
    u.bandWidth.value = Math.max(0.005, bandWidth);
    u.bandStrength.value = bandStrength;
    u.flash.value = flash;
    u.bandTint.value.setRGB(tint[0], tint[1], tint[2]);
  }

  /** 연출이 끝났을 때. 밴드를 화면 밖으로 밀고 세기를 0 으로 되돌린다. */
  clearFrontFx(): void {
    const u = this.matBack.uniforms as unknown as SurfaceUniforms;
    u.bandPos.value = -10;
    u.bandStrength.value = 0;
    u.flash.value = 0;
    u.bandTint.value.setRGB(1, 1, 1);
  }

  /** 평평한 상태로 복원 */
  resetFlat(): void {
    const a = this.pos.array as Float32Array;
    for (let i = 0; i < this.nVerts; i++) {
      a[i * 3] = this.rest[i * 2];
      a[i * 3 + 1] = this.rest[i * 2 + 1];
      a[i * 3 + 2] = this.zSign[i] !== 0 ? (this.zSign[i] * this.t) / 2 : this.rimZ[i];
    }
    this.pos.needsUpdate = true;
    this.geo.computeVertexNormals();
  }

  /**
   * 휘어짐 적용. 축(dx, dy) 방향으로 접점보다 앞선 구간이 bend 만큼 압축되며 카메라 쪽으로 부푼다.
   * 호 길이는 보존 → 화면상 길이가 짧아진다 (§7.2).
   * flipped: 카드가 X축 π 회전으로 구워진 상태 (로컬 y = -월드 y, 로컬 z = -월드 z)
   */
  applyBend(bend: number, contactProj: number, dx: number, dy: number, zSign: number): void {
    if (bend <= 1e-5) {
      this.resetFlat();
      return;
    }
    const m = Math.hypot(dx, dy) || 1;
    const ux = dx / m, uy = dy / m;
    // 축 방향으로 접점보다 앞선 구간(길이 La)만 압축되며 부푼다. 호 길이는 보존된다.
    const sMax = (this.w / 2) * Math.abs(ux) + (this.h / 2) * Math.abs(uy);
    const La = Math.max(1e-3, sMax - contactProj);
    const b = Math.min(bend, La * 0.9);
    const Lc = La - b;
    const A = (2 / Math.PI) * Math.sqrt(Math.max(0, Lc * b));
    const a = this.pos.array as Float32Array;
    for (let i = 0; i < this.nVerts; i++) {
      const x = this.rest[i * 2];
      const y = this.rest[i * 2 + 1];
      const zBase = this.zSign[i] !== 0 ? (this.zSign[i] * this.t) / 2 : this.rimZ[i];
      const proj = x * ux + y * uy;
      if (proj <= contactProj) {
        a[i * 3] = x;
        a[i * 3 + 1] = y;
        a[i * 3 + 2] = zBase;
        continue;
      }
      const s = (proj - contactProj) / La;
      const shift = contactProj + b + s * Lc - proj; // 축 방향으로 당겨지는 양 (음수)
      a[i * 3] = x + shift * ux;
      a[i * 3 + 1] = y + shift * uy;
      a[i * 3 + 2] = zBase + A * Math.sin(Math.PI * s) * zSign;
    }
    this.pos.needsUpdate = true;
    this.geo.computeVertexNormals();
  }
}

// ------------------------------------------------------------------ 팩 조각
export type JagFn = (x: number) => number;

/**
 * 절단선으로 나뉜 팩 조각 하나 (상단 또는 하단). 절단선 쪽 행이 지글거리는 프로파일을 따른다.
 * 좌표는 팩 로컬 (중심 원점). 상단 조각은 자기 중심(cx,cy) 기준으로 지오메트리를 만든다.
 */
export class PackPiece {
  geo: THREE.BufferGeometry;
  mesh: THREE.Mesh;
  pos: THREE.BufferAttribute;
  rest: Float32Array; // 팩 로컬 rest x,y
  nVerts: number;
  centerY = 0;
  jagRow: Float32Array; // 각 정점의 절단선까지 거리 가중치 (0..1, 1 = 절단선 위)
  distToJag: Float32Array; // 절단선까지 rest 거리 (unit)

  constructor(
    public w: number,
    public h: number,
    public isTop: boolean,
    public nx: number,
    public ny: number,
    map: THREE.Texture,
    public zOffset: number,
    gloss = 0.3,
    shade = 0.1,
  ) {
    const geo = new THREE.BufferGeometry();
    const n = (nx + 1) * (ny + 1);
    this.pos = new THREE.BufferAttribute(new Float32Array(n * 3), 3);
    this.pos.setUsage(THREE.DynamicDrawUsage);
    geo.setAttribute('position', this.pos);
    geo.setAttribute('uv', new THREE.BufferAttribute(new Float32Array(n * 2), 2));
    const index: number[] = [];
    for (let j = 0; j < ny; j++) {
      for (let i = 0; i < nx; i++) {
        const a = i + j * (nx + 1);
        const b = a + 1;
        const c = a + (nx + 1);
        const d = c + 1;
        index.push(a, b, d, a, d, c);
      }
    }
    geo.setIndex(index);
    this.geo = geo;
    this.rest = new Float32Array(n * 2);
    this.jagRow = new Float32Array(n);
    this.distToJag = new Float32Array(n);
    this.nVerts = n;
    const mat = makeSurfaceMaterial(map, gloss, shade);
    this.mesh = new THREE.Mesh(geo, mat);
    this.mesh.frustumCulled = false;
    this.mesh.position.z = zOffset;
  }

  get material(): THREE.ShaderMaterial {
    return this.mesh.material as THREE.ShaderMaterial;
  }

  /** 절단선 프로파일로 rest 좌표/UV 재구성. 상단 조각은 자기 중심 기준. */
  build(jag: JagFn, perfY: number): void {
    const { nx, ny, w, h } = this;
    const uv = this.geo.getAttribute('uv') as THREE.BufferAttribute;
    const ua = uv.array as Float32Array;
    this.centerY = this.isTop ? (perfY + h / 2) / 2 : 0;
    let k = 0;
    for (let j = 0; j <= ny; j++) {
      const f = j / ny;
      for (let i = 0; i <= nx; i++) {
        const x = -w / 2 + (w * i) / nx;
        const jy = jag(x);
        // 상단: 행 0 = 절단선, 행 ny = 상단 가장자리 / 하단: 행 0 = 하단 가장자리, 행 ny = 절단선
        const y = this.isTop ? jy + (h / 2 - jy) * f : -h / 2 + (jy + h / 2) * f;
        this.rest[k * 2] = x;
        this.rest[k * 2 + 1] = y - this.centerY;
        ua[k * 2] = x / w + 0.5;
        ua[k * 2 + 1] = y / h + 0.5;
        const dist = Math.abs(y - jy);
        this.distToJag[k] = dist;
        this.jagRow[k] = this.isTop ? 1 - f : f;
        k++;
      }
    }
    uv.needsUpdate = true;
    this.resetFlat();
  }

  resetFlat(): void {
    const a = this.pos.array as Float32Array;
    for (let i = 0; i < this.nVerts; i++) {
      a[i * 3] = this.rest[i * 2];
      a[i * 3 + 1] = this.rest[i * 2 + 1];
      a[i * 3 + 2] = 0;
    }
    this.pos.needsUpdate = true;
    this.geo.computeVertexNormals();
  }

  /**
   * 절단 변형. gapAt(x) = 이 조각의 절단선 정점이 밀려나는 양(unit, 부호 포함),
   * bulge(x, distToJag) = [dy, dz] 국소 융기 (양쪽 조각에 동일하게 적용해야 이음새가 안 벌어진다)
   */
  applyCut(gapAt: (x: number) => number, gapFalloff: number, bulge: (x: number, dist: number, out: Float64Array) => void): void {
    const a = this.pos.array as Float32Array;
    const tmp = new Float64Array(2);
    for (let i = 0; i < this.nVerts; i++) {
      const x = this.rest[i * 2];
      const y = this.rest[i * 2 + 1];
      const d = this.distToJag[i];
      const wgt = Math.max(0, 1 - d / gapFalloff);
      const g = gapAt(x) * wgt * wgt;
      bulge(x, d, tmp);
      a[i * 3] = x;
      a[i * 3 + 1] = y + g + tmp[0];
      a[i * 3 + 2] = tmp[1];
    }
    this.pos.needsUpdate = true;
    this.geo.computeVertexNormals();
  }
}
